import { CommonModule, DOCUMENT } from '@angular/common';
import { Capacitor } from '@capacitor/core';
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApplicationKnowledgeComponent, KNOWLEDGE_GRANTS_ENABLED_TOKEN, LoadingState } from '@galaxy/ai-knowledge';
import { TranslateService } from '@ngx-translate/core';
import { KnowledgeBaseInterface, KnowledgeSource, NamespaceInterface } from '@vendasta/embeddings';
import { GalaxyAiIconService } from '@vendasta/galaxy/ai-icon';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { AssistantKey, AssistantType } from '@vendasta/protos/ai_assistants/v1alpha1';
import { catchError, EMPTY, firstValueFrom, of, switchMap } from 'rxjs';
import { AiAssistantI18nModule } from '../../assets/i18n/ai-assistant-i18n.module';
import { AiAssistantPipesModule } from '../../pipes/src/pipes.module';
import { DEFAULT_ASSISTANT_NAMES, DEFAULT_AVATAR_SVG_ICON, TRY_IT_CTA_LABEL } from '../../core/ai-assistant.constants';
import { AiConnectionsForm, AiKnowledgeFormArray } from '../../core/forms';
import { ConnectionType } from '../../core/interfaces/ai-assistant.interface';
import { hasEnabledTrigger } from '../../core/utils/assistant-live-status';
import { SmsDetailsService } from '../../core/services/sms-details.service';
import { AiVoiceTryItModalComponent } from '../ai-voice-try-it-modal/ai-voice-try-it-modal.component';
import { aiAssistantAssetUrl } from '../../core/pipes/ai-assistant-asset-url.pipe';
import { AiAssistantService } from '../../core/services/ai-assistant.service';
import {
  autoReviewResponseTriggerEnabled,
  ReviewTriggerBannerService,
} from '../../core/services/review-trigger-banner.service';
import {
  customSuccessEvaluatorsEnabled,
  successEvaluatorsEnabled,
  supportsSuccessEvaluators,
} from '../../core/utils/success-evaluators';
import { JobsToBeDoneComponent } from '../jobs-to-be-done/jobs-to-be-done.component';
import { AiToolFormRegistryService } from '../../core/services/ai-tool-form-registry.service';
import { AiTalkPanelService } from '../../core/services/ai-talk-panel.service';
import { CanComponentDeactivate } from '../../core/services/unsaved-changes-guard-v2';
import { WebRTCCallService } from '../../core/services/webrtc-call.service';
import { ACCOUNT_GROUP_ID_TOKEN, PARTNER_ID_TOKEN } from '../../core/tokens';
import { AiAssistantFormV2Service } from '../ai-assistant-configuration/ai-assistant-form-v2/ai-assistant-form-v2.service';
import { AiConnectionsFormComponent } from '../ai-connections-form/ai-connections-form.component';
import { AiIncomingAutomationsComponent } from '../ai-incoming-automations/ai-incoming-automations.component';
import { StickyFooterComponent } from '../sticky-footer/sticky-footer.component';
import { assistantTypeLabelKey } from '../../core/utils/assistant-type-label';
import { MatDividerModule } from '@angular/material/divider';

@Component({
  selector: 'ai-assistant-summary-page',
  imports: [
    CommonModule,
    AiAssistantI18nModule,
    AiAssistantPipesModule,
    GalaxyPageModule,
    GalaxyAvatarModule,
    GalaxyEmptyStateModule,
    GalaxyLoadingSpinnerModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    RouterLink,
    AiConnectionsFormComponent,
    AiIncomingAutomationsComponent,
    ApplicationKnowledgeComponent,
    StickyFooterComponent,
    MatDividerModule,
    JobsToBeDoneComponent,
  ],
  // AiAssistantFormV2Service injects the tool form registry, so both are scoped to this page.
  providers: [AiAssistantFormV2Service, AiToolFormRegistryService, ReviewTriggerBannerService],
  templateUrl: './ai-assistant-summary-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './ai-assistant-summary-page.component.scss',
})
export class AiAssistantSummaryPageComponent implements CanComponentDeactivate {
  private readonly aiAssistantService = inject(AiAssistantService);
  private readonly translateService = inject(TranslateService);
  private readonly formV2Service = inject(AiAssistantFormV2Service);
  private readonly talkPanelService = inject(AiTalkPanelService);
  private readonly webrtcCallService = inject(WebRTCCallService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly aiIconService = inject(GalaxyAiIconService);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly accountGroupId = toSignal(inject(ACCOUNT_GROUP_ID_TOKEN));
  private readonly partnerId = toSignal(inject(PARTNER_ID_TOKEN));

  /**
   * Relative base for links out to the Automations product. One segment shallower than the
   * configuration editor's, because this route is `assistants/:assistantId`, not `.../edit`.
   */
  protected readonly automationsBase = '../../../';
  protected readonly defaultAvatarIcon = DEFAULT_AVATAR_SVG_ICON;
  /** Galaxy's avatar sets width/height inline, so the narrow-screen size is an override in the stylesheet. */
  protected readonly avatarSize = 96;
  protected readonly isNativeApp = Capacitor.isNativePlatform();
  protected readonly webrtcCallState = this.webrtcCallService.callState;

  protected readonly aiAssistant = toSignal(this.formV2Service.aiAssistant$);
  private readonly aiConnections = toSignal(this.formV2Service.aiConnections$);
  protected readonly blueprintAutomations = toSignal(this.formV2Service.blueprintAutomations$, { initialValue: [] });
  protected readonly fieldLocks = this.formV2Service.fieldLocks;

  private readonly routeAssistantId = toSignal(this.formV2Service.assistantId$, { initialValue: '' });

  /**
   * Also loading while the assistant on hand is not the one the route asked for. `aiAssistant$`
   * replays the previous value, so without this the page would render A's name, avatar, triggers and
   * knowledge under B's URL for as long as B was in flight.
   */
  protected readonly loading = computed(() => {
    const assistant = this.aiAssistant();
    if (assistant === undefined) return true;
    if (assistant === null) return false;
    return this.routeAssistantId() !== assistant.assistant?.id;
  });
  protected readonly assistantNotFound = computed(() => this.aiAssistant() === null);

  protected readonly assistantId = computed(() => this.aiAssistant()?.assistant?.id ?? '');
  protected readonly appId = computed(() => this.aiAssistantService.buildAppId(this.assistantId()));

  /** Hydration already fills the translated default name; this covers assistants with no default at all. */
  protected readonly name = computed(() => {
    const assistant = this.aiAssistant()?.assistant;
    return (
      assistant?.name || DEFAULT_ASSISTANT_NAMES.get(assistant?.type ?? AssistantType.ASSISTANT_TYPE_UNSPECIFIED) || ''
    );
  });

  private readonly typeLabelKey = computed(() => assistantTypeLabelKey(this.aiAssistant()?.assistant?.type));

  /**
   * Resolved through `stream` rather than `instant` so the comparison below re-runs when the bundle
   * lands or the language changes. Read synchronously, it returns the raw key until translations
   * load, the comparison misses, and the label sits under the name repeating it with nothing to
   * invalidate it.
   */
  private readonly translatedTypeLabel = toSignal(
    toObservable(this.typeLabelKey).pipe(switchMap((key) => (key ? this.translateService.stream(key) : of('')))),
    { initialValue: '' },
  );

  /**
   * Dropped when it would only repeat the name. Default assistants are named after their type
   * ("Chat Receptionist"), so the label would say the same word twice directly under itself.
   */
  protected readonly typeLabel = computed(() => {
    const label = this.translatedTypeLabel();
    return label && label !== this.name() ? label : null;
  });

  /**
   * Hydration copies a custom assistant's own description into `descriptionKey`, so one path covers
   * both cases. Passing free text through `translate` leaves it untouched, which is how the list
   * card renders the same field.
   */
  protected readonly descriptionKey = computed(() => {
    const assistant = this.aiAssistant();
    return assistant?.descriptionKey || assistant?.assistant?.description || '';
  });

  /**
   * Carried in from the default workforce by `hydrateAssistantWithDefaultInfo`. Custom assistants
   * have no default to inherit from, and the pipe floors anything out of range to level 1 — so an
   * unguarded card would label every custom assistant "Suggestive".
   */
  protected readonly autonomyRating = computed(() => this.aiAssistant()?.autonomyRating ?? 0);

  protected readonly avatarSrc = computed(() => {
    const assistant = this.aiAssistant();
    if (!assistant) return '';
    return (
      assistant.assistant.avatarUrl ||
      assistant.decoration?.avatarIconUrl ||
      aiAssistantAssetUrl(assistant.decoration?.defaultAvatarIcon, this.document)
    );
  });

  protected readonly configurationUrl = toSignal(
    this.formV2Service.aiAssistant$.pipe(
      switchMap((assistant) =>
        assistant?.assistant?.id
          ? this.aiAssistantService.buildAssistantConfigurationUrl(assistant.assistant.id)
          : of(''),
      ),
    ),
    { initialValue: '' },
  );

  protected readonly shouldShowChatButton = computed(() =>
    this.aiAssistantService.shouldShowChatButtonForAssistant(this.aiAssistant()),
  );

  protected readonly shouldShowCallButton = computed(() => {
    if (this.isNativeApp) return false;
    if (!this.aiAssistantService.isWebrtcVoiceEnabled()) return false;
    return this.aiAssistant()?.assistant?.type === AssistantType.ASSISTANT_TYPE_VOICE_RECEPTIONIST;
  });

  private readonly reviewTriggerBannerService = inject(ReviewTriggerBannerService);
  private readonly route = inject(ActivatedRoute);
  private readonly dialog = inject(MatDialog);
  private readonly claimedPhoneNumber = toSignal(
    inject(SmsDetailsService).phoneNumber$.pipe(catchError(() => of(''))),
    { initialValue: '' },
  );

  /**
   * "Try it" reaches the assistant the way a customer would. Two shapes, one per assistant:
   * webchat opens the microsite the widget is embedded on, voice hands over the number to call.
   *
   * The webchat URL rides in on the hydrated CTA, which the workforce providers only set once the
   * account actually has a microsite. Voice has no URL to link to, so it opens a modal instead.
   */
  private readonly webchatTryItUrl = computed(() => {
    const cta = this.aiAssistant()?.cta;
    return cta?.label === TRY_IT_CTA_LABEL ? (cta.action?.url ?? '') : '';
  });

  /** Nothing to try until a number is claimed and the phone channel actually routes to the assistant. */
  private readonly voiceTryItReady = computed(() => {
    const assistant = this.aiAssistant();
    if (assistant?.assistant?.type !== AssistantType.ASSISTANT_TYPE_VOICE_RECEPTIONIST) return false;
    if (!this.claimedPhoneNumber()) return false;
    const voiceConnections = this.aiConnections()?.filter(
      (aiConnection) => aiConnection.connection.connectionType === ConnectionType.Voice,
    );
    return hasEnabledTrigger(assistant.assistant.id, voiceConnections);
  });

  protected readonly shouldShowTryItButton = computed(() => !!this.webchatTryItUrl() || this.voiceTryItReady());

  protected onTryItClick(): void {
    const url = this.webchatTryItUrl();
    if (url) {
      // `window.open` does not imply noopener the way `target="_blank"` on an anchor does, so the microsite
      // would otherwise get a live handle back into this app.
      this.document.defaultView?.open(url, '_blank', 'noopener');
      return;
    }
    this.dialog.open(AiVoiceTryItModalComponent);
  }

  /** The flag, and then whether this assistant's type has a channel the evaluators can score at all. */
  protected readonly isSuccessEvaluatorsEnabled = computed(
    () =>
      successEvaluatorsEnabled(this.aiAssistantService, this.route) &&
      supportsSuccessEvaluators(this.aiAssistant()?.assistant?.type),
  );
  protected readonly isCustomSuccessEvaluatorsEnabled = computed(() =>
    customSuccessEvaluatorsEnabled(this.aiAssistantService, this.route),
  );

  private readonly jobsToBeDone = viewChild(JobsToBeDoneComponent);
  protected readonly jobsWindowDays = computed(() => this.jobsToBeDone()?.evaluationWindowDays() ?? 0);

  protected readonly jobsToBeDoneAssistantKey = computed<AssistantKey | undefined>(() => {
    const assistant = this.aiAssistant()?.assistant;
    if (!assistant?.id || !assistant.namespace) return undefined;
    return { id: assistant.id, namespace: assistant.namespace };
  });

  protected addJobClicked(): void {
    this.jobsToBeDone()?.openCreate();
  }

  protected readonly connectionsForm = signal<AiConnectionsForm | undefined>(undefined);
  /** Which assistant the page is currently showing, so a move to another one resets its state. */
  private shownAssistantId: string | null = null;
  protected readonly knowledgeForm = new AiKnowledgeFormArray([]);
  protected readonly knowledgeLoadStatus = signal<LoadingState>('loading');
  protected readonly knowledgeBasesDirty = signal(false);
  private readonly pendingKnowledgeBaseKeys = signal<{ knowledgeBaseId: string; namespace?: NamespaceInterface }[]>([]);

  protected readonly knowledgeGrantsEnabled = toSignal(
    inject(KNOWLEDGE_GRANTS_ENABLED_TOKEN, { optional: true }) ?? of(false),
    { initialValue: false },
  );

  /** Read for its dependency, not its value: dirtiness is answered by walking forms, which signals cannot track. */
  private readonly formRevision = signal(0);
  protected readonly saving = signal(false);

  protected readonly dirty = computed(() => {
    this.formRevision();
    return !!this.connectionsForm()?.dirty || this.knowledgeForm.dirty || this.knowledgeBasesDirty();
  });

  /**
   * Pending knowledge edits cannot be written until the control reports `succeeded` — saving earlier
   * would persist an empty source list. Hold the button rather than letting the press through and
   * reporting afterwards that part of it did not stick.
   */
  private readonly knowledgeNotSaveable = computed(() => {
    this.formRevision();
    return this.knowledgeForm.dirty && this.knowledgeLoadStatus() !== 'succeeded';
  });

  protected readonly submitDisabled = computed(() => !this.dirty() || this.saving() || this.knowledgeNotSaveable());

  /**
   * Followed the triggers card here off the configuration editor. `formRevision` is the dependency:
   * the toggle the banner keys off lives in a form, which signals cannot track.
   */
  protected readonly reviewTriggerBanner = computed(() => {
    this.formRevision();
    const toggles = new Map(
      (this.connectionsForm()?.controls ?? []).map((control) => [
        control.metadata.id,
        !!control.controls.connected.value,
      ]),
    );
    return this.reviewTriggerBannerService.bannerFor(
      autoReviewResponseTriggerEnabled(this.aiAssistant()?.assistant?.type, this.aiConnections(), toggles),
    );
  });

  constructor() {
    this.aiIconService.dummyInit();

    this.knowledgeForm.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.bumpRevision());

    // The router reuses this component when only `:assistantId` changes, so everything keyed to one
    // assistant has to be dropped on the way to the next. Left behind, the new assistant opens
    // already claiming unsaved changes it does not have.
    effect(() => {
      const assistantId = this.assistantId();
      if (!assistantId) return;
      untracked(() => {
        if (this.shownAssistantId === assistantId) return;
        this.shownAssistantId = assistantId;
        this.resetKnowledgeState();
        this.connectionsForm.set(undefined);
        this.bumpRevision();
      });
    });

    effect(() => {
      const connections = this.aiConnections();
      const assistantId = this.assistantId();
      if (!connections || !assistantId) return;
      untracked(() => {
        // A refresh must not throw away toggles the user has made but not saved. Moving to another
        // assistant clears the form above, so this only ever protects the assistant on screen.
        if (this.connectionsForm()?.dirty) return;
        this.connectionsForm.set(new AiConnectionsForm(connections, assistantId));
      });
    });

    // One subscription that follows whichever form is current: switchMap drops the previous form's
    // when it is replaced. Subscribing per form instead would leave every superseded form listened
    // to until the page was destroyed, and the list is rebuilt on each connections refresh.
    toObservable(this.connectionsForm)
      .pipe(
        switchMap((form) => form?.valueChanges ?? EMPTY),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.bumpRevision());
  }

  private bumpRevision(): void {
    this.formRevision.update((revision) => revision + 1);
  }

  /**
   * Clearing the parent's copy is only half of it: `ApplicationKnowledgeComponent` takes `appId` as a
   * plain input and loads from an effect on the account/partner ids, so a reused instance would never
   * refetch for the new assistant. What saves us is `loading()` going true during the switch, which
   * tears the card down and forces a fresh instance. Keep that gate if you touch it.
   */
  private resetKnowledgeState(): void {
    this.knowledgeForm.fromKnowledgeSources([], true);
    this.knowledgeForm.markAsPristine();
    this.knowledgeBasesDirty.set(false);
    this.pendingKnowledgeBaseKeys.set([]);
    this.knowledgeLoadStatus.set('loading');
  }

  protected async onChatButtonClick(): Promise<void> {
    await this.aiAssistantService.navigateToAssistantChat(this.assistantId(), this.router);
  }

  protected openVoiceChat(): void {
    const assistant = this.aiAssistant();
    if (!assistant) return;
    this.talkPanelService.openForAssistant({
      accountGroupId: this.accountGroupId() ?? null,
      partnerId: this.partnerId() ?? null,
      assistant,
    });
  }

  protected endVoiceChat(): void {
    this.talkPanelService.closeTalkPanel();
  }

  protected refreshBlueprintAutomations(): void {
    this.formV2Service.refreshBlueprintAutomations();
  }

  protected updateSelectedSources(
    opts: { sources: KnowledgeSource[] | null; initialLoad?: boolean } | undefined,
  ): void {
    const { sources, initialLoad = true } = opts ?? { sources: null };
    this.knowledgeForm.fromKnowledgeSources(sources ?? [], initialLoad);
    this.bumpRevision();
  }

  protected updateSelectedKnowledgeBases(opts: {
    knowledgeBases: KnowledgeBaseInterface[];
    initialLoad?: boolean;
  }): void {
    const { knowledgeBases, initialLoad = true } = opts;
    this.pendingKnowledgeBaseKeys.set(
      knowledgeBases.map((kb) => ({ knowledgeBaseId: kb.id ?? '', namespace: kb.namespace })),
    );
    if (!initialLoad) {
      this.knowledgeBasesDirty.set(true);
      this.bumpRevision();
    }
  }

  /**
   * Saves the three non-versioned concerns in the order the configuration editor uses. Each writes
   * through its own service, so one failure must not skip the others; the user is told at the end
   * whether everything landed.
   */
  protected async save(): Promise<void> {
    const assistant = this.aiAssistant()?.assistant;
    if (!assistant?.id || this.saving()) return;

    this.saving.set(true);
    // A save fired before the knowledge control has loaded would persist an empty source list.
    const knowledgeLoaded = this.knowledgeLoadStatus() === 'succeeded';
    // Unreachable through the button, which is disabled in this state; kept so a programmatic save
    // still cannot report success over an edit it silently dropped.
    let failed = this.knowledgeForm.dirty && !knowledgeLoaded;

    if (this.knowledgeForm.dirty && knowledgeLoaded) {
      try {
        await this.formV2Service.updateAssistantKnowledge(this.appId(), assistant.name || '', this.knowledgeForm);
        this.knowledgeForm.markAsPristine();
      } catch (e) {
        failed = true;
        console.error(`Error updating assistant knowledge: ${e}`);
      }
    }

    if (this.knowledgeGrantsEnabled() && this.knowledgeBasesDirty() && knowledgeLoaded) {
      try {
        await this.formV2Service.updateAssistantKnowledgeBases(
          this.appId(),
          assistant.name || '',
          this.pendingKnowledgeBaseKeys(),
        );
        this.knowledgeBasesDirty.set(false);
      } catch (e) {
        failed = true;
        console.error(`Error updating assistant knowledge bases: ${e}`);
      }
    }

    const connectionsForm = this.connectionsForm();
    if (connectionsForm?.dirty) {
      try {
        await firstValueFrom(
          this.formV2Service.updateConnections(
            { id: assistant.id, namespace: assistant.namespace },
            this.aiConnections() ?? [],
            connectionsForm.controls.map((connectionForm) => ({
              connectionId: connectionForm.metadata.id,
              enabled: connectionForm.controls.connected.value ?? false,
            })),
          ),
        );
        connectionsForm.markAsPristine();
      } catch (e) {
        failed = true;
        console.error(`Error updating assistant connections: ${e}`);
      }
    }

    this.bumpRevision();
    this.saving.set(false);

    if (failed) {
      this.snackbarService.openErrorSnack('AI_ASSISTANT.SUMMARY.SETTINGS_SAVE_ERROR');
    } else {
      this.snackbarService.openSuccessSnack('AI_ASSISTANT.SUMMARY.SETTINGS_SAVED');
    }
  }

  canDeactivate(): boolean {
    return !this.dirty();
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.dirty()) {
      event.preventDefault();
    }
  }
}
